import { ArrowLeftIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { useSearchSession } from '@entities/search-session';
import { isLive, useRequestOutreach, useSelection } from '@entities/selection';
import { KarlDialogue } from '@features/chat';
import { OutreachForm } from '@features/negotiation';
import { ApiError } from '@shared/api/problem';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { Button, ButtonLink, KarlIntro, Skeleton, StateBlock } from '@shared/ui';

import styles from './OutreachPage.module.css';

/**
 * N-01, экран 1 — «эти четыре подходят»: поручение на проверку (FE-170, MVP_PRD §21.5, §36).
 *
 * Шаг 4, до первого письма четырём автохаусам. Экран отвечает на один вопрос:
 * что Karl сделает от имени пользователя и у кого спросит. Состав вопросов и
 * текст письма пользователь не правит (решение заказчика от 09.09.2026).
 *
 * **Цен и условий здесь нет** — и это сказано прямо. Мандат на торг выдаётся
 * отдельно и только по выбранному автомобилю (§21.1).
 *
 * Автомобиль, по которому написать нельзя, показывается с конкретной причиной
 * и **не блокирует остальных**: замену подберёт backend тем же вызовом (§21.2).
 *
 * Вводный абзац под заголовком — реплика Karl (`KarlIntro`, DESIGN_SYSTEM
 * §7.2): что он сделает и что ответы останутся словами автохауса.
 */
export function OutreachPage() {
  const { t } = useTranslation();
  const { searchId = '' } = useParams();
  const navigate = useNavigate();

  const sessionQuery = useSearchSession(searchId || null);
  const selectionQuery = useSelection(searchId || null);
  const outreach = useRequestOutreach(searchId);

  const session = sessionQuery.data ?? null;
  const selection = selectionQuery.data ?? null;

  // Диалог с Karl — в рельсе, как на остальных экранах заказа (CLAUDE.md §8,
  // FE-581): уточнить запрос можно и перед поручением; пересборку подбора
  // запускает только кнопка в рельсе.
  useAppShell({
    stage: session?.session.stage ?? null,
    paid: session?.session.paid ?? false,
    karl: { extra: <KarlDialogue searchId={searchId} /> },
  });

  if (selectionQuery.isPending) {
    return (
      <div className={styles.page} aria-hidden="true">
        <Skeleton height="2.5rem" width="50%" />
        <Skeleton height="18rem" />
      </div>
    );
  }

  if (selectionQuery.isError || selection === null) {
    const error = selectionQuery.error;
    const unpaid = error instanceof ApiError && error.status === 402;
    return (
      <div className={styles.page}>
        <StateBlock
          tone={unpaid ? 'neutral' : 'error'}
          title={unpaid ? t('selection.locked.title') : t('selection.error.title')}
          description={unpaid ? t('selection.locked.text') : t('selection.error.text')}
          action={
            unpaid ? (
              <ButtonLink to={ROUTES.searchPayment(searchId)}>
                {t('selection.locked.action')}
              </ButtonLink>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  void selectionQuery.refetch();
                }}
              >
                {t('error.retry')}
              </Button>
            )
          }
        />
      </div>
    );
  }

  const slots = selection.slots.filter(isLive);

  if (slots.length === 0) {
    return (
      <div className={styles.page}>
        <StateBlock
          title={t('negotiation.outreach.emptyTitle')}
          description={t('negotiation.outreach.emptyText')}
          action={
            <ButtonLink to={ROUTES.searchTop4(searchId)} variant="secondary">
              {t('negotiation.outreach.backToTop4')}
            </ButtonLink>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Link to={ROUTES.searchTop4(searchId)} className={styles.back}>
        <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
        {t('negotiation.outreach.backToTop4')}
      </Link>

      <header className={styles.header}>
        <h1 className={styles.title}>{t('negotiation.outreach.title')}</h1>
        {/* Реплика Karl вместо вводного абзаца: что он сделает и что ответы
            останутся утверждениями автохауса. Текст фиксированный. */}
        <KarlIntro>
          {t('negotiation.outreach.subtitle', { count: slots.length })}{' '}
          {t('negotiation.outreach.lead')}
        </KarlIntro>
      </header>

      <OutreachForm
        slots={slots}
        busy={outreach.isPending}
        error={errorText(outreach.error, t)}
        onCancel={() => {
          void navigate(ROUTES.searchTop4(searchId));
        }}
        onConfirm={() => {
          outreach.mutate(
            { textHash: selection.outreach.text_hash },
            {
              onSuccess: () => {
                void navigate(ROUTES.searchTop4(searchId));
              },
            },
          );
        }}
      />
    </div>
  );
}

function errorText(error: unknown, t: (key: string) => string): string | null {
  if (!(error instanceof ApiError)) return null;
  const key = `negotiation.error.code.${error.code}`;
  const translated = t(key);
  return translated === key ? t('negotiation.error.text') : translated;
}
